import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Button } from '@cloudflare/kumo/components/button';
import { SignOut } from '@phosphor-icons/react';
import { useSession, authClient } from '@/lib/authClient';

interface Props {
  children: ReactNode;
}

// 管理ダッシュボードを保護。未ログイン → /sign-in、ログイン済だが role!=admin
// → 拒否表示 (ログアウト可能)。
export default function RequireAdmin({ children }: Props) {
  const { data, isPending } = useSession();
  const location = useLocation();

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!data?.user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?redirect=${redirect}`} replace />;
  }

  // 受付専用アカウントは受付端末の画面へ (ログイン後の既定の遷移先が / のため)
  if (data.user.role === 'kiosk') {
    return <Navigate to="/kiosk" replace />;
  }

  if (data.user.role !== 'admin') {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-4 px-6 py-16">
        <h1 className="text-2xl font-semibold text-kumo-default">アクセス権限がありません</h1>
        <Banner variant="error">
          このダッシュボードを利用できるのは管理者のみです。別アカウントでログインし直してください。
        </Banner>
        <p className="text-sm text-kumo-muted">
          ログイン中のメールアドレス: <strong>{data.user.email}</strong>
        </p>
        <div>
          <Button
            variant="secondary"
            icon={SignOut}
            onClick={async () => {
              await authClient.signOut();
              window.location.href = '/sign-in';
            }}
          >
            ログアウトして別アカウントで入り直す
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
